import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { PrimaryButton, SecondaryButton, SearchInput } from 'wf-react-component-library';
import './UserAdmin.scss';

const ITEMS_PER_PAGE = 10;

const UserAdmin = ({ onBack }) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [message, setMessage] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    vendorId: '',
    companyName: ''
  });

  const callAPI = async (payload) => {
    const user = await Auth.currentAuthenticatedUser();
    const token = user.signInUserSession.idToken.jwtToken;
    const apiEndpoint = process.env.REACT_APP_USER_MANAGEMENT_API_URL || '/api/user-management';

    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-api-authorization': token
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || `API call failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return data;
  };

  const searchUser = async () => {
    if (!searchEmail || searchEmail.trim().length < 3) {
      setMessage('Please enter at least 3 characters to search.');
      return;
    }
    setLoading(true);
    setMessage('');
    setSearchResults([]);
    setCurrentPage(1);
    try {
      const result = await callAPI({ action: 'search', query: searchEmail });
      setSearchResults(result.users || []);
      if ((result.users || []).length === 0) {
        setMessage('No users found.');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setSearchResults([]);
      setMessage('Error searching for user.');
    }
    setLoading(false);
  };

  const deleteUser = async (username, displayName, companyName) => {
    const label = companyName ? `${displayName} from ${companyName}` : displayName;
    if (!window.confirm(`Delete user ${label}?`)) return;
    try {
      await callAPI({ action: 'delete', username });
      setSearchResults(searchResults.filter(u => u.username !== username));
      setMessage('User deleted successfully.');
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage('Error deleting user.');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setMessage('');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setMessage('Error: Please enter a valid email address.');
      return;
    }

    try {
      await callAPI({
        action: 'create',
        email: newUser.email,
        first_name: newUser.firstName,
        last_name: newUser.lastName,
        vendor_code: newUser.vendorId,
        company_name: newUser.companyName
      });
      setNewUser({ email: '', firstName: '', lastName: '', vendorId: '', companyName: '' });
      setShowCreateForm(false);
      setMessage('User created successfully.');
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.status === 409) {
        setMessage('User already exists with that email.');
      } else {
        setMessage('Error creating user.');
      }
    }
  };

  return (
    <div className="user-admin">
      <div className="flex-container-row vendor-container">
        <div className="flex-container-column">
          <div className="headline-small">External Vendor User Management</div>
        </div>
        <div className="flex-container-column button-padding">
          <SecondaryButton onClick={onBack} name="Return to Dashboard" />
        </div>
      </div>

      {/* Search Section */}
      <div className="flex-container-row user-admin-section">
        <div className="flex-container-column full-width">
          <div className="flex-container-row wf-form-element-container">
            <label className="wf-form-element-label">Search User:</label>
          </div>
          <div className="flex-container-row search-row">
            <div className="search-container">
              <input
                type="text"
                className="wf-input"
                placeholder="Search by email, name, vendor ID..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUser()}
              />
            </div>
            <div className="button-padding">
              <PrimaryButton
                onClick={searchUser}
                name={loading ? 'Searching...' : 'Search'}
                isDisabled={!searchEmail || searchEmail.trim().length < 3 || loading}
              />
            </div>
            <div className="button-padding">
              <PrimaryButton
                onClick={() => setShowCreateForm(true)}
                name="+ Add New User"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className="flex-container-row user-admin-section">
          <div className={`user-admin-message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        </div>
      )}

      {/* Search Results Table */}
      {searchResults.length > 0 && (
        <div className="flex-container-row user-admin-section">
          <div className="flex-container-column full-width">
            <div className="flex-container-row item-count">
              {searchResults.length} user(s) found — Page {currentPage} of {Math.ceil(searchResults.length / ITEMS_PER_PAGE)}
            </div>
            <table className="item-table" cellPadding="0" cellSpacing="0" width="100%">
              <thead>
                <tr className="item-table-header">
                  <td>Email</td>
                  <td>Name</td>
                  <td>Company</td>
                  <td>Vendor ID</td>
                  <td>Status</td>
                  <td>Actions</td>
                </tr>
              </thead>
              <tbody>
                {searchResults
                  .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                  .map((user) => {
                  const isVendor = !user.username.startsWith('WholeFoodsMarket_');
                  return (
                  <tr key={user.username} className="item-row">
                    <td><span className="body-short-form">{user.email}</span></td>
                    <td><span className="body-short-form">{user.name}</span></td>
                    <td><span className="body-short-form">{user.companyName}</span></td>
                    <td><span className="body-short-form">{user.vendorId}</span></td>
                    <td><span className="body-short-form">{user.status}</span></td>
                    <td>
                      {isVendor ? (
                        <button
                          className="delete-action-btn"
                          onClick={() => deleteUser(user.username, user.name || user.email, user.companyName)}
                        >
                          Delete
                        </button>
                      ) : (
                        <button
                          className="delete-action-btn disabled"
                          title="Internal users cannot be deleted from this tool"
                          disabled
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {Math.ceil(searchResults.length / ITEMS_PER_PAGE) > 1 && (
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {Math.ceil(searchResults.length / ITEMS_PER_PAGE)}
                </span>
                <button
                  className="pagination-btn"
                  disabled={currentPage >= Math.ceil(searchResults.length / ITEMS_PER_PAGE)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create User Form */}
      {showCreateForm && (
        <div className="flex-container-row user-admin-section">
          <div className="flex-container-column full-width">
            <div className="flex-container-row wf-form-element-container">
              <label className="wf-form-element-label">Create New User:</label>
            </div>
            <form onSubmit={createUser} className="create-user-form">
              <div className="flex-container-row wf-form-element-container">
                <label className="wf-form-element-label" htmlFor="email">Email:</label>
                <input
                  id="email"
                  type="email"
                  className="wf-input"
                  placeholder="user@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                />
              </div>
              <div className="flex-container-row wf-form-element-container">
                <label className="wf-form-element-label" htmlFor="firstName">First Name:</label>
                <input
                  id="firstName"
                  type="text"
                  className="wf-input"
                  placeholder="First Name"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="flex-container-row wf-form-element-container">
                <label className="wf-form-element-label" htmlFor="lastName">Last Name:</label>
                <input
                  id="lastName"
                  type="text"
                  className="wf-input"
                  placeholder="Last Name"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                  required
                />
              </div>
              <div className="flex-container-row wf-form-element-container">
                <label className="wf-form-element-label" htmlFor="vendorId">Vendor ID:</label>
                <div className="input-with-help">
                  <input
                    id="vendorId"
                    type="text"
                    className="wf-input"
                    placeholder="e.g. AAORG, BOSTOCK"
                    value={newUser.vendorId}
                    onChange={(e) => setNewUser({...newUser, vendorId: e.target.value.toUpperCase()})}
                    required
                    pattern="[A-Z0-9]+"
                  />
                  <span className="input-help-text">Must match an existing active vendor code in P3. Invalid codes will prevent the user from accessing VMA apps.</span>
                </div>
              </div>
              <div className="flex-container-row wf-form-element-container">
                <label className="wf-form-element-label" htmlFor="companyName">Company:</label>
                <input
                  id="companyName"
                  type="text"
                  className="wf-input"
                  placeholder="Company Name"
                  value={newUser.companyName}
                  onChange={(e) => setNewUser({...newUser, companyName: e.target.value})}
                  required
                />
              </div>
              <div className="flex-container-row edit-action-buttons">
                <PrimaryButton onClick={createUser} name="Create User" />
                <SecondaryButton onClick={() => setShowCreateForm(false)} name="Cancel" />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdmin;
